import { Button } from "@sellar-io/components";
import { ReactElement, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Icon, SpinnerLegacy } from "../../../components";
import {
  DialogContent,
  DialogCTA,
  DialogPage,
} from "../../../components/modal/Dialog";
import { useFullPageDialogContext } from "../../../components/modal/useFullPageDialogContext";
import { useBuyerUpdate } from "../../../hooks/api/v2/retailer/useBuyerUpdate";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { BuyerVenueSelection } from "../BuyerVenueSelection";
import { BuyerVenueSelectOther } from "../BuyerVenueSelectOther";
import { BuyerVenueTypesDialogPage } from "./BuyerVenueTypesDialog.shared";

export function BuyerVenueTypesDialogContent({
  onDismiss,
}: {
  onDismiss: () => void;
}): ReactElement | null {
  const { business } = useSellarCurrentBusiness();
  const { setPage, page } = useFullPageDialogContext();
  const buyerVenueTypes = business?.Retailer?.venueTypes;
  const [selectedVenueTypes, setSelectedVenueTypes] = useState<
    string[] | undefined
  >(buyerVenueTypes);
  if (selectedVenueTypes === undefined && buyerVenueTypes) {
    setSelectedVenueTypes(buyerVenueTypes);
  }
  const buyerUpdateMutation = useBuyerUpdate(business?.Retailer?.id ?? 0);
  const form = useForm({
    defaultValues: {
      venueType: null,
    },
  });
  const venueType = useWatch({
    control: form.control,
    name: "venueType",
  });

  const onSubmit = () => {
    let finalisedSelectedVenueTypes = selectedVenueTypes;
    if (venueType && selectedVenueTypes?.includes("other")) {
      finalisedSelectedVenueTypes = [
        ...(selectedVenueTypes
          ? selectedVenueTypes.filter((type) => type !== "other")
          : []),
        venueType,
      ];
      setSelectedVenueTypes(finalisedSelectedVenueTypes);
    }
    buyerUpdateMutation.mutate(
      {
        venueTypes: finalisedSelectedVenueTypes,
      },
      {
        onSuccess: () => onDismiss(),
      },
    );
  };

  const isLoading = buyerUpdateMutation.isPending;

  return (
    <>
      {isLoading ? (
        <SpinnerLegacy />
      ) : (
        <>
          <DialogPage page={BuyerVenueTypesDialogPage.FIRST}>
            <DialogContent>
              <BuyerVenueSelection
                selectedVenueTypes={selectedVenueTypes}
                setSelectedVenueTypes={setSelectedVenueTypes}
              />
            </DialogContent>
          </DialogPage>
          <DialogPage page={BuyerVenueTypesDialogPage.SECOND}>
            <DialogContent>
              <BuyerVenueSelectOther form={form} />
            </DialogContent>
          </DialogPage>
        </>
      )}
      <DialogCTA>
        {page === BuyerVenueTypesDialogPage.FIRST ? (
          <>
            <div className="flex flex-row justify-between">
              <Button
                disabled={isLoading}
                onClick={() => {
                  if (selectedVenueTypes?.includes("other")) {
                    setPage(BuyerVenueTypesDialogPage.SECOND);
                  } else {
                    onSubmit();
                  }
                }}
              >
                {selectedVenueTypes?.includes("other") ? "Next" : "Confirm"}
              </Button>
            </div>
          </>
        ) : (
          <>
            <Button
              disabled={isLoading}
              color={"tertiary"}
              onClick={() => {
                setPage(BuyerVenueTypesDialogPage.FIRST);
              }}
            >
              <Icon icon={"CHEVRON_LEFT"} />
              back
            </Button>
            <Button
              disabled={!venueType || isLoading}
              onClick={() => {
                if (venueType) {
                  onSubmit();
                }
              }}
            >
              Confirm
            </Button>
          </>
        )}
      </DialogCTA>
    </>
  );
}
